import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { SupportChains } from '@/config/constants';
import AesUtils from '@/framework/AesUtils';
import { signMessage } from '@/utils';
import HttpUtils from '@/framework/HttpUtils';
import { AccountInfo } from '@/framework/Application';
import GatewayHttpUtils from '@/framework/GatewayHttpUtils';
import { canReuseWalletLogin, createWalletSessionKey, isSameWalletSession } from '../wallet-session';
import { normalizeWalletLoginError, WalletLoginError } from '../wallet-login-error';
import { clearWalletLoginLock, tryAcquireWalletLoginLock, waitForWalletLoginToken } from '../wallet-login-lock';

export const REDUCER_NAME = 'wallet';
export enum WalletType {
    METAMASK = 'METAMASK',
    WALLET_CONNECTOR = 'WALLET_CONNECTOR',
    NONE = 'NONE',
}

export enum TransactionState {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAIL = 'FAIL',
}

export interface WalletModel {
    chainId: number;
    address: string;
    connected: boolean;
    type?: string;
    chain?: WalletChain;
    logged: boolean;
}

export interface WalletTransactionModel extends WalletModel {
    transactions: TransactionItem[];
    trading: boolean;
    disconnect: number;
    loginPending: boolean;
    loginError?: WalletLoginError;
    token?: LocalToken;
}

export const AccountContext = {
    Logged: false,
    Connected: false,
    WalletType: WalletType.NONE,
    Address: '',
    Token: '',
    Expired: 0,
} as AccountInfo;

const LastLoginAddress = 'wallet-last-login-address';
const LastLoginChainId = 'wallet-last-login-chain-id';
const LoginToken = 'wallet-login-token';

const ClearToken = function () {
    localStorage.removeItem(LastLoginAddress);
    localStorage.removeItem(LastLoginChainId);
    localStorage.removeItem(LoginToken);
};

const GetToken = function (): LocalToken | undefined {
    let localStr = localStorage.getItem(LoginToken);
    if (localStr) {
        localStr = AesUtils.Decrypt(localStr);
    }
    if (localStr) {
        return JSON.parse(localStr);
    } else {
        localStorage.removeItem(LoginToken);
    }
    return undefined;
};

export const link = createAsyncThunk<void, WalletModel>(REDUCER_NAME + '/link', async (data: WalletModel, { dispatch, getState }) => {
    const loginSessionKey = createWalletSessionKey(data);
    const isCurrentWallet = () => {
        const current = ((getState() as any)[REDUCER_NAME] as WalletTransactionModel) || {};
        return current.address?.toLowerCase() === data.address.toLowerCase() && current.chainId === data.chainId;
    };
    const state = (getState() as any)[REDUCER_NAME] as WalletTransactionModel;
    if (state.loginPending && isSameWalletSession(state, data)) {
        return;
    }
    const savedChainId = localStorage.getItem(LastLoginChainId);
    const savedSession = {
        address: localStorage.getItem(LastLoginAddress) || undefined,
        chainId: savedChainId ? Number(savedChainId) : undefined,
    };
    if (state.logged || localStorage.getItem(LoginToken)) {
        // Address or chain changes must force a fresh wallet signature.
        if (!canReuseWalletLogin(savedSession, data)) ClearToken();
    }
    // 链接钱包
    dispatch(connect(data));
    const token = GetToken();
    // 如果token存在，则直接登录
    if (token) {
        dispatch(login(token));
        return;
    }
    // 签名
    dispatch(loginStart());
    const lock = tryAcquireWalletLoginLock(loginSessionKey);
    if (!lock.acquired) {
        const sharedToken = await waitForWalletLoginToken(GetToken, {
            timeoutMs: lock.expiresAt ? Math.max(lock.expiresAt - Date.now(), 0) : undefined,
            isCurrentSession: isCurrentWallet,
        });
        if (!isCurrentWallet()) {
            return;
        }
        if (sharedToken) {
            dispatch(login(sharedToken));
            return;
        }
        dispatch(loginFail(normalizeWalletLoginError('WAIT_LOGIN_TIMEOUT')));
        return;
    }
    try {
        const t = parseInt(`${new Date().getTime() / 1000}`);
        const msg = await signMessage(`${t},${t}`);
        if (!isCurrentWallet()) return;
        const tk = await HttpUtils.Post('login', { parameters: { random: t }, signature: msg }, { CurrentTime: t });
        if (!isCurrentWallet()) return;
        dispatch(login(tk as any));
        localStorage.setItem(LastLoginAddress, data.address);
        localStorage.setItem(LastLoginChainId, String(data.chainId));
        localStorage.setItem(LoginToken, AesUtils.Encrypt(JSON.stringify(tk)));
    } catch (error) {
        if (!isCurrentWallet()) return;
        ClearToken();
        dispatch(loginFail(normalizeWalletLoginError(error)));
    } finally {
        clearWalletLoginLock(loginSessionKey);
    }
});

const walletSlice = createSlice({
    name: REDUCER_NAME,
    initialState: {
        address: '',
        chainId: -1,
        connected: false,
        trading: false,
        loginPending: false,
        transactions: [],
        disconnect: 0,
        logged: false,
    } as WalletTransactionModel,
    reducers: {
        connect: (state, action: PayloadAction<WalletModel>) => {
            state.address = action.payload.address;
            state.chainId = action.payload.chainId;
            state.type = action.payload.type;
            state.connected = true;
            state.logged = false;
            state.loginPending = false;
            state.loginError = undefined;
            const chain = SupportChains.filter((o) => o.chainId == action.payload.chainId)[0];
            state.chain = Object.assign({}, chain);
            HttpUtils.SetAccount({
                WalletType: state.type,
                Chain: state.chain.name,
                Address: state.address,
                Language: AccountContext.Language,
            });
            AccountContext.Logged = false;
            AccountContext.Address = state.address;
            AccountContext.Chain = state.chain ? state.chain.name : '';
            AccountContext.Connected = true;
            AccountContext.Token = '';
            AccountContext.Expired = 0;
        },
        loginStart: (state) => {
            state.loginPending = true;
            state.loginError = undefined;
        },
        login: (state, action: PayloadAction<LocalToken>) => {
            state.token = action.payload;
            state.token.Address = state.address;
            state.logged = true;
            state.loginPending = false;
            state.loginError = undefined;
            HttpUtils.Login(action.payload.token);
            GatewayHttpUtils.Login(action.payload.token);
            AccountContext.Logged = true;
            AccountContext.Address = state.address;
            AccountContext.Chain = state.chain ? state.chain.name : '';
            AccountContext.Connected = true;
            AccountContext.Token = action.payload.token;
            AccountContext.Expired = action.payload.expired;
            AccountContext.WalletType = WalletType.NONE;
        },
        loginFail: (state, action: PayloadAction<WalletLoginError>) => {
            state.token = undefined;
            state.logged = false;
            state.loginPending = false;
            state.loginError = action.payload;
            AccountContext.Logged = false;
            AccountContext.Token = '';
            AccountContext.Expired = 0;
        },
        dismissLoginError: (state) => {
            state.loginError = undefined;
        },
        unlink: (state) => {
            if (!state.logged && !state.connected) {
                return;
            }
            state.address = '';
            state.chainId = -1;
            state.type = undefined;
            state.connected = false;
            state.chain = undefined;
            state.logged = false;
            state.loginPending = false;
            state.loginError = undefined;
            state.token = undefined;
            AccountContext.Logged = false;
            AccountContext.Address = '';
            AccountContext.Chain = '';
            AccountContext.Connected = false;
            AccountContext.Token = '';
            AccountContext.Expired = 0;
            AccountContext.WalletType = WalletType.NONE;
            ClearToken();
            HttpUtils.UnLogin();
        },
        disconnect: (state) => {
            state.disconnect++;
        },
        setTrading: (state, action: PayloadAction<boolean>) => {
            state.trading = action.payload;
        },
    },
});

export const { connect, loginStart, login, loginFail, dismissLoginError, unlink, setTrading, disconnect } = walletSlice.actions;
export default walletSlice.reducer;
